<script setup lang="ts">
import {
  BoxComponent,
  CampaignSelector,
  type ContentItemsFilter,
  CreatorSelector,
  DateRangeSelector,
  FlexComponent,
  orderContentTypes,
  platformContentTypeOptions,
  platformOfContentTypes,
  platformToContentTypes,
  SegmentedFilter,
  type SegmentedFilterItem,
  SortingComponent,
  SourceSelector,
  type SupportedContentItemPlatform,
} from '@metapic-apps/shared'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { useGetCampaigns } from '@/api/campaigns.api'
import { loadCreatorsForContentLibrary } from '@/api/users.api'
import InstagramIcon from '@/assets/icons/instagram.svg?component'
import SnapchatIcon from '@/assets/icons/snapchat-costs-icon.svg?component'
import TiktokIcon from '@/assets/icons/tiktok.svg?component'
import { useSortingItems } from '@/lib/hooks'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useEntrypointStore } from '@/stores/entrypoint.stores'
import { useGlobalFilterStore } from '@/stores/global-filter.stores'

const SOURCE_ITEMS: SegmentedFilterItem<SupportedContentItemPlatform>[] = [
  { label: 'Instagram', value: 'instagram', icon: InstagramIcon },
  { label: 'Snapchat', value: 'snapchat', icon: SnapchatIcon },
  { label: 'TikTok', value: 'tiktok', icon: TiktokIcon },
]

const filter = defineModel<ContentItemsFilter>('modelValue', { required: true })
const { sortingItems } = useSortingItems()
const store = useStore()
const filterStore = useGlobalFilterStore()
const advertiserStore = useAdvertiserStore()
const entrypointStore = useEntrypointStore()
const advertiser = computed(() => advertiserStore?.advertiser)
const hideUsers = computed(() => store.getters['shop/hideUsers'])
const hideEdit = computed(() => store.getters['shop/hideEdit'])
const hasCampaigns = computed(() => entrypointStore.hasCampaigns)

const page = ref(1)
const campaignSearchQuery = ref('')

const { data: campaigns, isLoading: isCampaignsLoading } = useGetCampaigns(advertiser, page, campaignSearchQuery)

const showCampaignSelector = computed(() => !hideEdit.value || hasCampaigns.value)

const source = computed<SupportedContentItemPlatform | undefined>({
  get: () => platformOfContentTypes(filter.value.contentTypes),
  set: (platform) => {
    filter.value.contentTypes = platform ? platformToContentTypes(platform) : undefined
  },
})

// `SourceSelector` models the selection as plain strings; `orderContentTypes` narrows it back.
const contentTypes = computed<string[] | undefined>({
  get: () => filter.value.contentTypes,
  set: (value) => {
    filter.value.contentTypes = value?.length ? orderContentTypes(value) : undefined
  },
})
</script>
<template>
  <BoxComponent>
    <FlexComponent justify="space-between" align="center">
      <SegmentedFilter v-model="source" :items="SOURCE_ITEMS" aria-label="Content source" />
      <FlexComponent wrap="wrap" align="center" :gap="4">
        <CreatorSelector
          v-if="!hideUsers"
          v-model:creator="filter.creator"
          :advertiser-group-id="advertiser.storeGroupId"
          :advertiser-id="advertiser.id"
          :load-creators="loadCreatorsForContentLibrary"
          :width="350"
          should-truncate-text
          placeholder="Search by creator"
        />
        <CampaignSelector
          v-if="showCampaignSelector"
          v-model:campaign="filter.campaign"
          :campaigns="campaigns"
          :is-loading="isCampaignsLoading"
          :query="campaignSearchQuery"
          name-key="name"
          @update:query="campaignSearchQuery = $event"
        />
        <SourceSelector v-model:content-types="contentTypes" :content-type-options="platformContentTypeOptions" label="Type" />
        <DateRangeSelector :date-range="filterStore.dateRange" @update:date-range="filterStore.setDateRange" />
        <SortingComponent v-model="filter.sortBy" :items="sortingItems" has-separate-order-sorting />
      </FlexComponent>
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
:deep(.el-tooltip__trigger) {
  cursor: pointer;
  &:hover,
  &[aria-expanded='true'] {
    border-radius: 8px;
    background: var(--button-hover);
  }
  i {
    transition: all 0.3s ease-in;
  }

  &[aria-expanded='true'] {
    .chevron-icon {
      transform: rotate(180deg);
    }
  }
}
:global(.date-range-selector__popper) {
  transform: translateX(-10px);
}
</style>
